import { Checkbox, Col, DatePickerProps, Row, Select, Tooltip } from 'antd'
import clsx from 'clsx'
import { PrintIcon, Invoice } from '../../assets/icons'
import { ButtonCommon, DatePickerCommon, IconWrap } from '../../components'
import { PageHeader } from '../../components/PageHeader'
import styles from './index.module.scss'
import InvoiceManager from '../../assets/images/InvoiceManager.png'
import { ColumnsType } from 'antd/lib/table'
import { IInvoice } from '../../models'
import { ManagerModal } from './Modal'
import { useEffect, useRef, useState } from 'react'
import React from 'react'
import { PaginationCommon } from '../../components/Pagination'
import { DataEmpty } from '../../components/DataEmpty'
import { useReactToPrint } from 'react-to-print'
import { CheckboxChangeEvent } from 'antd/lib/checkbox'
import { Loading } from '../../components/Loading'
import moment from 'moment'
import { useGetInvoices, usePrintInvoice } from '../../hooks'
import InvoidDetail from './InvoidDetail'
import { convertNumberToMoneyFormat, getTypeMeter } from '../../until'
import { useTranslation } from 'react-i18next'
import { getColumnTitle } from '../../until/helper/getColumnTitle'
import CustomTable from '../../components/Table'

const InvoicePage = () => {
  const [visible, setVisible] = useState(false)
  const componentRef = useRef(null)
  const [checkAll, setCheckAll] = useState(false)
  const [checkedList, setCheckedList] = useState<any[]>([])
  const [indeterminate, setIndeterminate] = useState(false)
  const [currentInvoice, setCurrentInvoice] = useState<IInvoice | null>(null)
  const [currentInvoiceId, setCurrentInvoiceId] = useState<string | number | null>(null)
  const {t,i18n} = useTranslation()
  const typeMeter = getTypeMeter(t)
  const [params, setParams] = useState<{
    page: number
    page_size: number
    date: string | null
    type: string | null
  }>({
    page: 1,
    page_size: 100,
    date: null,
    type: null,
  })

  const columns: ColumnsType<IInvoice> = [
    {
      title: getColumnTitle('invoiceManager.invoiceNo', styles.page__content__title),
      dataIndex: 'id',
      key: 'invoice_no',
      width:  125,
      render: (text: string, record: IInvoice) => {
        return (
          <a onClick={() => {
            setCurrentInvoice(record)
            setVisible(!visible)
            setCurrentInvoiceId(text)
          }}>{`#${text}`}</a>
        )
      }
    },
    {
      title: getColumnTitle('invoiceManager.buildingName', styles.page__content__title),
      dataIndex: 'building_name',
      key: 'building_name',
      width:  155,
      render: (_text: string, record: IInvoice) => {
        if (!record?.building_name) {
          return ''
        }

        return (
          <div className={styles.text__name__container} style={{ maxWidth: '224px'}}>
            <Tooltip title={record?.building_name} placement="bottomLeft" overlayStyle={{ minWidth: 50 }}>
              <span>{record?.building_name}</span>
            </Tooltip>
          </div>
        )
      }
    },
    {
      title: getColumnTitle('invoiceManager.buildingNo', styles.page__content__title),
      dataIndex: 'building_code',
      key: 'building_code',
      width: 95,
      render: (_text: string, record: IInvoice) => {
        if (!record?.building_code) {
          return ''
        }

        return (
          <div className={styles.text__name__container} style={{ maxWidth: '80px' }}>
            <Tooltip title={record?.building_code} placement="bottomLeft" overlayStyle={{ minWidth: 50 }}>
              <span>{record?.building_code}</span>
            </Tooltip>
          </div>
        )
      }
    },
    {
      title: getColumnTitle('invoiceManager.roomNo', styles.page__content__title),
      dataIndex: 'roomNo',
      key: 'roomNo',
      width: 95,
      render: (_text: string, record: IInvoice) => {
        if (!record?.room_no) {
          return ''
        }

        return (
          <div className={styles.text__name__container} style={{ maxWidth: '80px' }}>
            <Tooltip title={record?.room_no} placement="bottomLeft" overlayStyle={{ minWidth: 50 }}>
              <span>{record?.room_no}</span>
            </Tooltip>
          </div>
        )
      }
    },
    {
      title: getColumnTitle('invoiceManager.type', styles.page__content__title),
      dataIndex: 'meter_type',
      key: 'meter_type',
      width: 85,
      render: (_text: string, record: IInvoice) =>  {
        return typeMeter.find((item: any) => item.value === Number(record?.meter_type))?.name ?? ''
      }
    },
    {
      title: getColumnTitle('invoiceManager.date', styles.page__content__title),
      dataIndex: 'date',
      key: 'date',
      width: 115,
    },
    {
      title: getColumnTitle('invoiceManager.employee', styles.page__content__title),
      dataIndex: 'employee',
      key: 'employee',
      width: 100,
      render: (_text: string, record: IInvoice) => {
        if (!record?.worker) {
          return ''
        }

        return (
          <div className={styles.text__name__container} style={{ maxWidth: '100px' }}>
            <Tooltip title={record?.worker} placement="bottomLeft" overlayStyle={{ minWidth: 50 }}>
              <span>{record?.worker}</span>
            </Tooltip>
          </div>
        )
      }
    },
    {
      title: getColumnTitle('invoiceManager.previousScanDate', styles.page__content__title),
      dataIndex: 'start_time',
      key: 'start_time',
      width: 130,
    },
    {
      title: getColumnTitle('invoiceManager.quantity', styles.page__content__title),
      dataIndex: 'quantity',
      key: 'quantity',
      width: 100,
      render: (_text: string, record: IInvoice) => {
        return record?.total_use ??  ''
      }
    },
    {
      title: getColumnTitle('invoiceManager.total', styles.page__content__title),
      dataIndex: 'total',
      key: 'total',
      width: 100,
      render: (_text: string, record: IInvoice) => {
        return convertNumberToMoneyFormat(record.cost) ?? ''
      }
    },
    {
      title: getColumnTitle('invoiceManager.history', styles.page__content__title),
      dataIndex: 'history',
      key: 'history',
      width: 90,
      render: (_, record) => {
        return (
          <span className={clsx([
            {[styles.textDeleted]: record?.deleted_flag},
            {[styles.textModify]: record?.edit_flag && !record?.deleted_flag},
          ])}>
            {record?.deleted_flag ? t('invoiceManager.deleted') : record?.edit_flag ? t('invoiceManager.modify') : ''}
          </span>
        )
      },
    },
  ]

  const { data, isLoading, isFetching, refetch } = useGetInvoices(params)
  const { mutateAsync: mutatePrintAsync, isLoading: isPrintLoading } = usePrintInvoice()

  useEffect(() => {
    setCheckedList([])
    setIndeterminate(false)
    setCheckAll(false)
    refetch()
  }, [i18n.language, refetch])

  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
    pageStyle: `
      @page { size: A4 Portrait }
      @media print {
        body { 
          -webkit-print-color-adjust: exact; 
        }
      }
    `
  })

  const onCheckAllChange = (e: CheckboxChangeEvent) => {
    const listInvoices = data?.data.map((item: any) => item)
    setCheckedList(e.target.checked && listInvoices?.length ? listInvoices : [])
    setIndeterminate(false)
    setCheckAll(e.target.checked)
  }

  const onCheckAllReset = () => {
    if (!checkAll) return
    setCheckedList([])
    setIndeterminate(false)
    setCheckAll(false)
  }

  const onChangePagination = (page: number, page_size: number) => {
    setCheckedList([])
    setIndeterminate(false)
    setCheckAll(false)
    if (page_size !== params.page_size) {
      return setParams({ ...params, page: 1, page_size })
    }
    setParams({ ...params, page, page_size })
  }

  const handleChangeSelectMeterType = (value: string) => {
    onCheckAllReset()
    setParams({
      ...params,
      page: 1,
      type: value,
    })
  }

  const handleConvertBillDetail = async () => {
    await mutatePrintAsync()
    handlePrint()
  }

  const handleChangeDate: DatePickerProps['onChange'] = (date, dateString) => {
    onCheckAllReset()
    if (!date) {
      setParams({ ...params, page: 1, date: null })
      return
    }
    setParams({ ...params, page: 1, date: dateString })
  }

  const rowSelection = {
    selectedRowKeys: checkedList.map((item) => item.id),
    columnWidth: 35,
    onChange: (_selectedRowKeys: React.Key[], selectedRows: any[]) => {
      if (selectedRows.length > 0) {
        setIndeterminate(true)
      }
      if (selectedRows.length === data?.data.length) {
        setIndeterminate(false)
        setCheckAll(true)
      }
      if (selectedRows.length === 0) {
        setIndeterminate(false)
        setCheckAll(false)
      }
      setCheckedList(selectedRows)
    },
  }

  return (
    <div className={styles.page__container}>
      <PageHeader height='108px' title={t('home.invoiceManager')} backgroundImage={InvoiceManager} />
      <Loading isLoading={isLoading || isFetching} />
      <Row wrap={false} className={clsx(styles.page__content__filter)}>
        <Col>
          <Row
            wrap={false}
            align='middle'
            gutter={40}
            className='select__group'
          >
            <Col className={clsx(styles.page__content__filter__userType)}>
              <span style={{ fontWeight: 400, fontSize: '14px' }}>{t('invoiceManager.searchType')}</span>
              <Select
                autoFocus={false}
                maxTagCount='responsive'
                allowClear
                value={params?.type}
                optionLabelProp='label'
                style={{ minWidth: 156 }}
                placeholder={
                  <>
                    <IconWrap color='#8C8C8C' icon={<Invoice />} className={clsx(styles.icon__filter)} />
                    {t('invoiceManager.searchTypePlaceholder')}
                  </>
                }
                onChange={handleChangeSelectMeterType}
              >
                {typeMeter.map((type: any) => (
                  <Select.Option
                    key={type.name}
                    value={type.value}
                    label={
                      <React.Fragment>
                        <IconWrap color='#8C8C8C' icon={<Invoice />} className={clsx(styles.icon__filter)} />
                        {type.name}
                      </React.Fragment>
                    }
                  >
                    {type.name}
                  </Select.Option>
                ))}
              </Select>
            </Col>

            <Col className={clsx(styles.page__content__filter__userType)}>
              <span style={{ fontWeight: 400, fontSize: '14px' }}>{t('invoiceManager.date')}</span>
              <DatePickerCommon
                onChange={handleChangeDate}
                placeholder={t('invoiceManager.dateTime')}
                value={params.date ? moment(params?.date) : null}
                format='YYYY/MM/DD'
              />
            </Col>
          </Row>
        </Col>

        <Col className={clsx(styles.page__content__filter__printQR)}>
          <Checkbox
            indeterminate={indeterminate}
            onChange={onCheckAllChange}
            checked={checkAll}
            disabled={!data?.data || data?.data?.length === 0}
          >
            {t('invoiceManager.selectAll')}
          </Checkbox>
          <ButtonCommon
            disabled={checkedList.length === 0}
            className={styles.printqrcode__btn}
            icon={<IconWrap icon={<PrintIcon />} size={18} />}
            type='ghost'
            onClick={handleConvertBillDetail}
            loading={isPrintLoading}
          >
            {t('invoiceManager.printInvoice')}
          </ButtonCommon>
        </Col>
      </Row>

      {!isLoading && (
        <div className={clsx(styles.page__content)}>
          {data?.data && data.data.length > 0 ? (
            <>
              <CustomTable
                rowSelection={{
                  type: 'checkbox',
                  ...rowSelection,
                }}
                rowKey='id'
                columns={columns}
                dataSource={data.data}
                pagination={false}
                scroll={{ x: 1200, y: 'calc(100vh - 400px)' }}
                tableLayout='fixed'
              />
              <div className={styles.pagination}>
                <PaginationCommon
                  defaultCurrent={1}
                  total={data.pagination.total}
                  current={data.pagination.current_page}
                  pageSize={data.pagination.per_page}
                  onChangePagination={onChangePagination}
                  pageSizeOptions={['10', '20', '50', '100']}
                  showSizeChanger
                />
              </div>
            </>
          ) : (
            <DataEmpty className={styles.empty__results} ><></></DataEmpty>
          )}
        </div>
      )}

      {visible && currentInvoice && (
        <ManagerModal
          title={t('invoiceManager.invoiceDetail')}
          visible={true}
          invoiceId={currentInvoiceId as string}
          onOk={() => {
            setVisible(false)
            setCurrentInvoice(null)
          }}
          onCancel={() => {
            setVisible(false)
            setCurrentInvoice(null)
          }}
          isLoading={false}
          invoice={currentInvoice}
        />
      )}
      <div className={styles.list__invoice}>
        <div ref={componentRef} >
          {checkedList &&
            checkedList.map((item: any, idx: number) => (
              <div key={idx} className={clsx(['pagebreak', styles.invoice__deatail])}>
                <InvoidDetail invoice={item} />
              </div>
            ))}
        </div>
      </div>
    </div>
  )
}

export default InvoicePage
